package com.schoolconnect.app

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material3.BottomAppBar
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DrawerValue
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalDrawerSheet
import androidx.compose.material3.ModalNavigationDrawer
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.rememberDrawerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.movableContentOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.datasource.network.NetworkActivityTracker
import com.schoolconnect.app.designsystem.ApiLoadingOverlay
import com.schoolconnect.app.designsystem.AppErrorHost
import com.schoolconnect.app.designsystem.AppSuccessToastHost
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.runtime.AppErrorCenter
import com.schoolconnect.app.runtime.AuthSessionCenter
import com.schoolconnect.app.feature.common.FeatureEntry
import com.schoolconnect.app.feature.common.featureCatalog
import com.schoolconnect.app.feature.common.FeatureRouteHost
import com.schoolconnect.app.feature.common.AppScreenTransition
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.common.LocalAppLogoutInProgress
import com.schoolconnect.app.feature.common.LocalProfileAction
import com.schoolconnect.app.feature.common.ShellProfileStore
import com.schoolconnect.app.feature.home.EditProfileModal
import com.schoolconnect.app.feature.home.HomeUserProfileUiState
import com.schoolconnect.app.feature.home.toEditProfileFormValues
import com.schoolconnect.app.feature.home.HomeFeatureRoute
import com.schoolconnect.app.feature.itusers.ITUsersFeatureRoute
import com.schoolconnect.app.feature.rolepicker.RolePickerDialog
import com.schoolconnect.app.feature.splash.SplashFeatureRoute
import com.schoolconnect.app.feature.schools.AddSchoolScreen
import com.schoolconnect.app.feature.schools.edit.EditSchoolFeatureRoute
import com.schoolconnect.app.feature.schoolusers.SchoolUsersFeatureRoute
import com.schoolconnect.app.navigation.FeatureRoute
import com.schoolconnect.app.navigation.HomeRoute
import com.schoolconnect.app.state.AppViewModel
import com.schoolconnect.app.util.DebugPreview
import com.schoolconnect.app.theme.SchoolConnectTheme
import com.schoolconnect.app.util.scLog
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.ic_dark_mode
import ischoolconnect.sharedui.generated.resources.ic_light_mode
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.painterResource
import kotlin.time.Clock

@Composable
fun App() {
    val viewModel = remember { AppViewModel() }
    val uiState by viewModel.uiState.collectAsState()
    val blockingNetworkRequestsInFlight by NetworkActivityTracker.blockingInFlightCount.collectAsState()
    var isApiLoaderVisible by remember { mutableStateOf(false) }
    var apiLoaderStartedAtMs by remember { mutableStateOf(0L) }
    var showGlobalEditProfile by remember { mutableStateOf(false) }
    val shellProfile by ShellProfileStore.profile.collectAsState()
    val successToastHostState = remember { SnackbarHostState() }

    DisposableEffect(viewModel) {
        onDispose { viewModel.clear() }
    }

    LaunchedEffect(Unit) {
        viewModel.bootstrapSession()
    }

    LaunchedEffect(Unit) {
        AuthSessionCenter.sessionExpiredEvents.collect {
            viewModel.forceLogin()
        }
    }

    LaunchedEffect(uiState.currentRoute, uiState.isAuthenticated) {
        scLog(
            "[DATA_RETRIEVE_SC] UI/App route=${uiState.currentRoute} authenticated=${uiState.isAuthenticated} user=${uiState.currentUserId} role=${uiState.homeRoleCode} school=${uiState.homeRoleSchoolId}"
        )
    }

    LaunchedEffect(uiState.feedbackNonce) {
        val message = uiState.feedbackMessage.trim()
        if (message.isNotBlank()) {
            if (message.startsWith("Session terminated successfully")) {
                AppToastCenter.showSuccess(message)
            } else {
                AppErrorCenter.emit(message, source = "App")
            }
        }
    }

    LaunchedEffect(blockingNetworkRequestsInFlight) {
        if (blockingNetworkRequestsInFlight > 0) {
            apiLoaderStartedAtMs = Clock.System.now().toEpochMilliseconds()
            isApiLoaderVisible = true
        } else if (isApiLoaderVisible) {
            val elapsedMs = Clock.System.now().toEpochMilliseconds() - apiLoaderStartedAtMs
            val minVisibleMs = 420L
            if (elapsedMs < minVisibleMs) {
                delay(minVisibleMs - elapsedMs)
            }
            isApiLoaderVisible = false
        }
    }

    Scaffold(
        snackbarHost = {
            androidx.compose.foundation.layout.Column(
                horizontalAlignment = androidx.compose.ui.Alignment.CenterHorizontally
            ) {
                AppSuccessToastHost(hostState = successToastHostState)
            }
        }
    ) { outerPadding ->
        SchoolConnectTheme(useDarkTheme = uiState.isDarkTheme) {
            CompositionLocalProvider(
                LocalAppLogoutInProgress provides uiState.isLoggingOut,
                LocalProfileAction provides { showGlobalEditProfile = true }
            ) {
                Box(modifier = Modifier.fillMaxSize().padding(outerPadding)) {
                    val debugFeatureId = DebugPreview.featureId()
                    when (debugFeatureId) {
                        "add-school" -> AddSchoolScreen(
                            onBack = {},
                            onSaveSchool = { _ -> }
                        )
                        "edit-school" -> EditSchoolFeatureRoute(
                            onBack = {},
                            onSaved = {}
                        )
                        "school-users" -> SchoolUsersFeatureRoute(onBack = {})
                        "staff" -> HomeFeatureRoute(
                            initialSelectedNavId = "staff",
                            homeSchoolId = uiState.homeRoleSchoolId,
                            onBack = {},
                            onHomeRoleChanged = viewModel::updateHomeRole
                        )
                        "it-users" -> ITUsersFeatureRoute(onBack = {})
                        "academic-years" -> HomeFeatureRoute(
                            initialSelectedNavId = "academic-years",
                            homeSchoolId = uiState.homeRoleSchoolId,
                            onBack = {},
                            onHomeRoleChanged = viewModel::updateHomeRole
                        )
                        "students" -> HomeFeatureRoute(
                            initialSelectedNavId = "students",
                            homeSchoolId = uiState.homeRoleSchoolId,
                            onBack = {},
                            onHomeRoleChanged = viewModel::updateHomeRole
                        )
                        "parents" -> HomeFeatureRoute(
                            initialSelectedNavId = "parents",
                            homeSchoolId = uiState.homeRoleSchoolId,
                            onBack = {},
                            onHomeRoleChanged = viewModel::updateHomeRole
                        )
                        else -> {
                            if (uiState.isBootstrapping) {
                                SplashFeatureRoute()
                                if (uiState.startupRolePicker.isVisible) {
                                    RolePickerDialog(
                                        state = uiState.startupRolePicker,
                                        onOptionSelected = { viewModel.selectStartupRole(it.id) },
                                        onRememberSelectionChanged = { _ -> },
                                        onContinueClicked = { _ -> viewModel.confirmStartupRoleSelection() },
                                        onCloseClicked = { }
                                    )
                                }
                            } else if (!uiState.isAuthenticated) {
                                scLog("[DATA_RETRIEVE_SC] UI/App rendering auth route currentRoute=${uiState.currentRoute}")
                                FeatureRouteHost(
                                    featureId = "auth",
                                    onBack = {},
                                    onAuthenticated = viewModel::completeAuthentication,
                                    onOpenFeature = viewModel::openFeature,
                                    onToggleTheme = viewModel::toggleTheme,
                                    onLogout = viewModel::logout,
                                    homeSchoolId = uiState.homeRoleSchoolId,
                                    features = featureCatalog
                                )
                            } else {
                                AppShell(
                                    features = featureCatalog,
                                    isDarkTheme = uiState.isDarkTheme,
                                    currentRoute = uiState.currentRoute,
                                    homeRoleCode = uiState.homeRoleCode,
                                    homeSchoolLabel = uiState.homeRoleSchoolLabel.ifBlank { "iConnect.School" },
                                    homeSchoolId = uiState.homeRoleSchoolId,
                                    homeSchoolCode = uiState.homeRoleSchoolCode,
                                    restoredRailId = uiState.restoredRailId,
                                    onToggleTheme = viewModel::toggleTheme,
                                    onOpenHome = viewModel::openHome,
                                    onOpenFeature = viewModel::openFeature,
                                    onLogout = viewModel::logout,
                                    onAuthenticated = viewModel::completeAuthentication,
                                    onHomeRoleChanged = viewModel::updateHomeRole
                                )
                            }
                        }
                    }

                    EditProfileModal(
                        open = showGlobalEditProfile,
                        initialValues = HomeUserProfileUiState(
                            fullName = shellProfile.fullName,
                            email = shellProfile.email,
                            mobileNumber = shellProfile.mobileNumber,
                            employeeId = shellProfile.employeeId,
                            schoolRole = shellProfile.schoolRole,
                            authRole = shellProfile.authRole,
                            mobileVerified = shellProfile.mobileVerified,
                            emailVerified = shellProfile.emailVerified,
                            profileImageUrl = shellProfile.profileImageUrl,
                            pushNotificationsEnabled = shellProfile.pushNotificationsEnabled,
                            emailNotificationsEnabled = shellProfile.emailNotificationsEnabled,
                            isLoading = false
                        ).toEditProfileFormValues(),
                        roleCode = uiState.homeRoleCode,
                        onClose = { showGlobalEditProfile = false },
                        onSave = { values ->
                            viewModel.saveCurrentProfile(values)
                            showGlobalEditProfile = false
                        }
                    )

                    AppErrorHost()

                    ApiLoadingOverlay(
                        isVisible = isApiLoaderVisible || uiState.isLoggingOut,
                        title = if (uiState.isLoggingOut) "Signing out" else "Updating data",
                        subtitle = if (uiState.isLoggingOut) {
                            "Closing your session securely."
                        } else {
                            "Please wait while iConnect.School finishes the request."
                        }
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun AppShell(
    features: List<FeatureEntry>,
    isDarkTheme: Boolean,
    currentRoute: Any,
    homeRoleCode: String,
    homeSchoolLabel: String,
    homeSchoolId: String,
    homeSchoolCode: String,
    restoredRailId: String,
    onToggleTheme: () -> Unit,
    onOpenHome: () -> Unit,
    onOpenFeature: (String) -> Unit,
    onLogout: () -> Unit,
    onAuthenticated: (String, String, String) -> Unit,
    onHomeRoleChanged: (String, String, String) -> Unit
) {
    val isHome = currentRoute == HomeRoute
    val appContent = remember(
        currentRoute,
        homeRoleCode,
        homeSchoolLabel,
        homeSchoolId,
        homeSchoolCode,
        restoredRailId,
        onOpenHome,
        onOpenFeature,
        onToggleTheme,
        onLogout,
        onAuthenticated,
        onHomeRoleChanged,
        features
    ) {
        movableContentOf {
            AppContent(
                features = features,
                currentRoute = currentRoute,
                homeRoleCode = homeRoleCode,
                homeSchoolLabel = homeSchoolLabel,
                homeSchoolId = homeSchoolId,
                homeSchoolCode = homeSchoolCode,
                initialSelectedNavId = restoredRailId,
                onOpenHome = onOpenHome,
                onOpenFeature = onOpenFeature,
                onToggleTheme = onToggleTheme,
                onLogout = onLogout,
                onAuthenticated = onAuthenticated,
                onHomeRoleChanged = onHomeRoleChanged
            )
        }
    }

    if (currentRoute is FeatureRoute && currentRoute.featureId == "students") {
        HomeFeatureRoute(
            homeRoleCode = homeRoleCode,
            homeSchoolLabel = homeSchoolLabel,
            homeSchoolId = homeSchoolId,
            homeSchoolCode = homeSchoolCode,
            initialSelectedNavId = "students",
            features = features,
            onOpenFeature = onOpenFeature,
            onToggleTheme = onToggleTheme,
            onLogout = onLogout,
            onHomeRoleChanged = onHomeRoleChanged,
            onBack = onOpenHome
        )
        return
    }
    if (currentRoute is FeatureRoute && currentRoute.featureId == "parents") {
        HomeFeatureRoute(
            homeRoleCode = homeRoleCode,
            homeSchoolLabel = homeSchoolLabel,
            homeSchoolId = homeSchoolId,
            homeSchoolCode = homeSchoolCode,
            initialSelectedNavId = "parents",
            features = features,
            onOpenFeature = onOpenFeature,
            onToggleTheme = onToggleTheme,
            onLogout = onLogout,
            onHomeRoleChanged = onHomeRoleChanged,
            onBack = onOpenHome
        )
        return
    }

    if (isHome) {
        appContent()
        return
    }

    val drawerState = rememberDrawerState(initialValue = DrawerValue.Closed)
    val scope = rememberCoroutineScope()
    val currentFeatureId = (currentRoute as? FeatureRoute)?.featureId
    val currentFeature = features.firstOrNull { it.id == currentFeatureId }
    val sidebarFeatures = features.filterNot { it.id == "home" }

    if (currentFeatureId == "academic-years") {
        HomeFeatureRoute(
            homeRoleCode = homeRoleCode,
            homeSchoolLabel = homeSchoolLabel,
            homeSchoolId = homeSchoolId,
            homeSchoolCode = homeSchoolCode,
            initialSelectedNavId = "academic-years",
            features = features,
            onOpenFeature = onOpenFeature,
            onToggleTheme = onToggleTheme,
            onLogout = onLogout,
            onHomeRoleChanged = onHomeRoleChanged,
            onBack = onOpenHome
        )
        return
    }
    if (currentFeatureId == "parents") {
        HomeFeatureRoute(
            homeRoleCode = homeRoleCode,
            homeSchoolLabel = homeSchoolLabel,
            homeSchoolId = homeSchoolId,
            homeSchoolCode = homeSchoolCode,
            initialSelectedNavId = "parents",
            features = features,
            onOpenFeature = onOpenFeature,
            onToggleTheme = onToggleTheme,
            onLogout = onLogout,
            onHomeRoleChanged = onHomeRoleChanged,
            onBack = onOpenHome
        )
        return
    }

    ModalNavigationDrawer(
        drawerState = drawerState,
        drawerContent = {
            ModalDrawerSheet(
                modifier = Modifier.widthIn(max = 320.dp)
            ) {
                Column(
                    modifier = Modifier.padding(20.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "iConnect.School",
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        text = "Feature map",
                        style = MaterialTheme.typography.labelLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                SidebarItem(
                    label = "Home",
                    badge = "H",
                    selected = isHome,
                    onClick = {
                        onOpenHome()
                        scope.launch { drawerState.close() }
                    }
                )
                HorizontalDivider()
                LazyColumn(
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(vertical = 12.dp)
                ) {
                    items(sidebarFeatures, key = { it.id }) { feature ->
                        SidebarItem(
                            label = feature.title,
                            badge = feature.title,
                            selected = currentFeatureId == feature.id,
                            onClick = {
                                onOpenFeature(feature.id)
                                scope.launch { drawerState.close() }
                            }
                        )
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
                SidebarItem(
                    label = if (isDarkTheme) "Light theme" else "Dark theme",
                    badge = if (isDarkTheme) "L" else "D",
                    selected = false,
                    onClick = onToggleTheme
                )
            }
        }
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            val compact = maxWidth < 900.dp
            if (compact) {
                Scaffold(
                    topBar = {
                        TopAppBar(
                            title = {
                                Text(
                                    text = if (isHome) "Home" else currentFeature?.title ?: "Module",
                                    fontWeight = FontWeight.SemiBold
                                )
                            },
                            navigationIcon = {
                                IconButton(onClick = { scope.launch { drawerState.open() } }) {
                                    Icon(Icons.Filled.Menu, contentDescription = "Open navigation")
                                }
                            },
                            actions = {
                            IconButton(onClick = onToggleTheme) {
                                Icon(
                                    painter = painterResource(if (isDarkTheme) Res.drawable.ic_light_mode else Res.drawable.ic_dark_mode),
                                    contentDescription = "Toggle theme"
                                )
                            }
                            },
                            colors = TopAppBarDefaults.topAppBarColors(
                                containerColor = MaterialTheme.colorScheme.surface
                            )
                        )
                    },
                    bottomBar = {
                        BottomAppBar(
                            actions = {
                                IconButton(onClick = onOpenHome) {
                                    Icon(Icons.Filled.Home, contentDescription = "Home")
                                }
                                IconButton(onClick = {
                                    onOpenFeature(sidebarFeatures.firstOrNull()?.id ?: "home")
                                }) {
                                    FeatureBadge("All")
                                }
                                IconButton(onClick = onToggleTheme) {
                                    Icon(
                                        painter = painterResource(if (isDarkTheme) Res.drawable.ic_light_mode else Res.drawable.ic_dark_mode),
                                        contentDescription = "Toggle theme"
                                    )
                                }
                            }
                        )
                    }
                ) { innerPadding ->
                    Box(modifier = Modifier.fillMaxSize().padding(innerPadding)) {
                        appContent()
                    }
                }
            } else {
                Row(modifier = Modifier.fillMaxSize()) {
                    Column(
                        modifier = Modifier
                            .width(280.dp)
                            .fillMaxSize()
                            .padding(horizontal = 12.dp, vertical = 16.dp)
                    ) {
                        Text(
                            text = "iConnect.School",
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )
                        Text(
                            text = "Feature map",
                            style = MaterialTheme.typography.labelLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Spacer(modifier = Modifier.height(12.dp))
                        SidebarItem(
                            label = "Home",
                            badge = "H",
                            selected = isHome,
                            onClick = onOpenHome
                        )
                        HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp))
                        LazyColumn(
                            modifier = Modifier.weight(1f).fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            items(sidebarFeatures, key = { it.id }) { feature ->
                                SidebarItem(
                                    label = feature.title,
                                    badge = feature.title,
                                    selected = currentFeatureId == feature.id,
                                    onClick = { onOpenFeature(feature.id) }
                                )
                            }
                        }
                        HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp))
                        SidebarItem(
                            label = if (isDarkTheme) "Light theme" else "Dark theme",
                            badge = if (isDarkTheme) "L" else "D",
                            selected = false,
                            onClick = onToggleTheme
                        )
                    }
                    HorizontalDivider(modifier = Modifier.width(1.dp))
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxSize()
                    ) {
                        appContent()
                    }
                }
            }
        }
    }
}

@Composable
private fun AppContent(
    features: List<FeatureEntry>,
    currentRoute: Any,
    homeRoleCode: String,
    homeSchoolLabel: String,
    homeSchoolId: String,
    homeSchoolCode: String,
    initialSelectedNavId: String,
    onOpenHome: () -> Unit,
    onOpenFeature: (String) -> Unit,
    onToggleTheme: () -> Unit,
    onLogout: () -> Unit,
    onAuthenticated: (String, String, String) -> Unit,
    onHomeRoleChanged: (String, String, String) -> Unit,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
    ) {
        AppScreenTransition(targetState = currentRoute) { routeState ->
            when (val route = routeState) {
            HomeRoute -> FeatureRouteHost(
                featureId = "home",
                onBack = onOpenHome,
                onAuthenticated = onAuthenticated,
                onOpenFeature = onOpenFeature,
                onToggleTheme = onToggleTheme,
                onLogout = onLogout,
                onHomeRoleChanged = onHomeRoleChanged,
                homeRoleCode = homeRoleCode,
                homeSchoolLabel = homeSchoolLabel,
                homeSchoolId = homeSchoolId,
                homeSchoolCode = homeSchoolCode,
                initialSelectedNavId = initialSelectedNavId,
                features = features
            )

            is FeatureRoute -> {
                FeatureRouteHost(
                    featureId = route.featureId,
                    onBack = onOpenHome,
                    onAuthenticated = onAuthenticated,
                    onOpenFeature = onOpenFeature,
                    onToggleTheme = onToggleTheme,
                    onLogout = onLogout,
                    onHomeRoleChanged = onHomeRoleChanged,
                    homeRoleCode = homeRoleCode,
                    homeSchoolLabel = homeSchoolLabel,
                    homeSchoolId = homeSchoolId,
                    homeSchoolCode = homeSchoolCode,
                    initialSelectedNavId = initialSelectedNavId,
                    features = features
                )
            }

            else -> FeatureRouteHost(
                featureId = "home",
                onBack = onOpenHome,
                onAuthenticated = onAuthenticated,
                onOpenFeature = onOpenFeature,
                onToggleTheme = onToggleTheme,
                onLogout = onLogout,
                onHomeRoleChanged = onHomeRoleChanged,
                homeRoleCode = homeRoleCode,
                homeSchoolLabel = homeSchoolLabel,
                homeSchoolId = homeSchoolId,
                homeSchoolCode = homeSchoolCode,
                initialSelectedNavId = initialSelectedNavId,
                features = features
            )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun SidebarItem(
    label: String,
    badge: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = if (selected) {
                MaterialTheme.colorScheme.primaryContainer
            } else {
                MaterialTheme.colorScheme.surface
            }
        ),
        shape = RoundedCornerShape(24.dp),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            FeatureBadge(badge)
            Text(
                text = label,
                style = MaterialTheme.typography.bodyLarge,
                fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                color = if (selected) {
                    MaterialTheme.colorScheme.onPrimaryContainer
                } else {
                    MaterialTheme.colorScheme.onSurface
                }
            )
        }
    }
}

@Composable
private fun FeatureBadge(text: String) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.primaryContainer
        )
    ) {
        Box(
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = text.take(1).uppercase(),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onPrimaryContainer,
                fontWeight = FontWeight.Bold
            )
        }
    }
}
